// src/routes/AppRoutes.js
import React from "react";
import { Routes, Route } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import ProtectedRoute from "../components/ProtectedRoute";
import LoadingScreen from "../components/LoadingScreen";
import ScrollTrackingWrapper from "../components/ScrollTrackingWrapper";

// Pages
import Home from "../pages/Home";
import Blog from "../pages/Blog";
import PostPage from "../pages/PostPage";
import SkillsTopics from "../pages/SkillsTopics";
import TopicPage from "../pages/topics/index";
import About from "../pages/About";
import AboutNew from "../pages/AboutNew";
import Arts from "../pages/Arts";
import Cart from "../pages/Cart";
import Orders from "../pages/Orders";
import Contact from "../pages/Contact";
import AuthContact from "../pages/AuthContact";
import Gadgets from "../pages/Gadgets";
import Account from "../pages/Account";
import Terms from "../pages/Terms";
import Privacy from "../pages/Privacy";
import Disclaimer from "../pages/Disclaimer";
import ContentEditor from "../pages/ContentEditor/ContentEditor";
import Chatty from "../pages/Chatty";
import ProjectsPage from "../pages/ProjectsPage";
import ProjectHome from "../pages/ProjectHome";
import NotFound from "../components/NotFound";

// Auth Pages — login / register / forgot-password bounce to the apex (limkc.com
// is the single auth surface; no duplicate cards on app.limkc.com). Only Logout
// and VerifyEmail still render on the app.
import RedirectToApex from "../auth/RedirectToApex";
import VerifyEmail from "../auth/VerifyEmail";
import Logout from "../auth/Logout";

// Dashboard Pages
import AdminDashboard from "../dashboard/AdminDashboard";
import UserDashboard from "../dashboard/UserDashboard";
import AdminUsers from "../dashboard/AdminUsers";
// import AdminAnalytics from "../dashboard/AdminAnalytics"; // Currently unused
import UserUsage from "../dashboard/UserUsage";
import Analytics from "../pages/Analytics";

// Agent Pages
import Agents from "../pages/Agents";
import WebUIChatInterface from "../components/chat/webui/WebUIChatInterface";

// Integrations
import IntegrationGithub from "../pages/IntegrationGithub";

// Lazy load Stripe pages to avoid loading Stripe JS on every page
const Checkout = React.lazy(() => import("../pages/Checkout"));
const CheckoutSuccess = React.lazy(() => import("../pages/CheckoutSuccess"));

const AppRoutes = ({
  messages,
  setMessages,
  setMessagesForSession,
  isLoading,
  setIsLoading,
  setIsLoadingForSession,
  isFocusMode,
  handleScrollChange,
  isMobile,
  currentOrientation,
  registerChat,
  chatSidebarOpen,
  setChatSidebarOpen,
}) => {
  const { user, loading } = useAuth();

  // Helper function to wrap components with scroll tracking
  const withScrollTracking = (Component, props = {}) => (
    <ScrollTrackingWrapper
      handleScrollChange={handleScrollChange}
      isMobile={isMobile}
      currentOrientation={currentOrientation}
    >
      <Component {...props} />
    </ScrollTrackingWrapper>
  );

  return (
    <Routes>
      {/* Sitemap route: render empty fragment to satisfy element prop */}
      <Route path="/sitemap.xml" element={<></>} />
      <Route 
        path="/contact" 
        element={loading ? 
          <LoadingScreen /> : 
          withScrollTracking(user ? AuthContact : Contact)
        } 
      />
      <Route path="/editor" element={withScrollTracking(ContentEditor)} />
      <Route path="/" element={withScrollTracking(Home)} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute 
            element={withScrollTracking(UserDashboard)} 
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']} 
          />
        } 
      />
      <Route 
        path="/admin-dashboard" 
        element={
          <ProtectedRoute 
            element={withScrollTracking(AdminDashboard)} 
            allowedMemberships={['admin', 'owner']} 
          />
        } 
      />
      <Route 
        path="/admin-dashboard/users" 
        element={
          <ProtectedRoute 
            element={withScrollTracking(AdminUsers)} 
            allowedMemberships={['admin', 'owner']} 
          />
        } 
      />
      <Route
        path="/dashboard/usage"
        element={
          <ProtectedRoute
            element={withScrollTracking(UserUsage)}
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']}
          />
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute
            element={withScrollTracking(Analytics)}
            allowedMemberships={['admin', 'owner']}
          />
        }
      />
      <Route 
        path="/account" 
        element={
          <ProtectedRoute 
            element={withScrollTracking(Account)} 
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']} 
          />
        } 
      />
      <Route 
        path="/agents" 
        element={
          <ProtectedRoute 
            element={withScrollTracking(Agents)} 
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']} 
          />
        } 
      />
      <Route
        path="/agents/chatty"
        element={
          <ProtectedRoute
            element={<WebUIChatInterface />}
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']}
          />
        }
      />
      <Route
        path="/integrations/github"
        element={
          <ProtectedRoute
            element={<IntegrationGithub />}
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']}
          />
        }
      />
      <Route path="/blog" element={withScrollTracking(Blog)} />
      <Route path="/blog/:postId" element={withScrollTracking(PostPage)} />
      <Route path="/projects" element={withScrollTracking(SkillsTopics)} />
      <Route path="/topics/:topicId" element={withScrollTracking(TopicPage)} />
      <Route path="/about" element={withScrollTracking(AboutNew)} />
      <Route path="/about-old" element={withScrollTracking(About)} />
      <Route path="/gadgets" element={withScrollTracking(Gadgets)} />
      <Route path="/arts" element={withScrollTracking(Arts)} />
      <Route path="/cart" element={withScrollTracking(Cart)} />
      <Route path="/checkout" element={<React.Suspense fallback={<LoadingScreen />}>{withScrollTracking(Checkout)}</React.Suspense>} />
      <Route path="/checkout/success" element={<React.Suspense fallback={<LoadingScreen />}>{withScrollTracking(CheckoutSuccess)}</React.Suspense>} />
      <Route path="/orders" element={withScrollTracking(Orders)} />
      <Route
        path="/chatty"
        element={
          withScrollTracking(Chatty, {
            messages,
            setMessages,
            setMessagesForSession,
            isLoading,
            setIsLoading,
            setIsLoadingForSession,
            isFocusMode,
            sidebarOpen: chatSidebarOpen,
            setSidebarOpen: setChatSidebarOpen,
          })
        }
      />
      <Route
        path="/chatty/:sessionId"
        element={
          withScrollTracking(Chatty, {
            messages,
            setMessages,
            setMessagesForSession,
            isLoading,
            setIsLoading,
            setIsLoadingForSession,
            isFocusMode,
            sidebarOpen: chatSidebarOpen,
            setSidebarOpen: setChatSidebarOpen,
          })
        }
      />
      <Route
        path="/chatty/projects"
        element={
          <ProtectedRoute
            element={withScrollTracking(ProjectsPage, {
              sidebarOpen: chatSidebarOpen,
              setSidebarOpen: setChatSidebarOpen,
            })}
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']}
          />
        }
      />
      <Route
        path="/chatty/projects/:projectId"
        element={
          <ProtectedRoute
            element={withScrollTracking(ProjectHome, {
              sidebarOpen: chatSidebarOpen,
              setSidebarOpen: setChatSidebarOpen,
            })}
            allowedMemberships={['standard', 'pro', 'ultimate', 'admin', 'owner']}
          />
        }
      />
      <Route path="/login" element={<RedirectToApex to="/login" />} />
      <Route path="/register" element={<RedirectToApex to="/register" />} />
      <Route path="/verify-email" element={withScrollTracking(VerifyEmail)} />
      <Route path="/logout" element={withScrollTracking(Logout)} />
      <Route path="/forgot-password" element={<RedirectToApex to="/forgot-password" />} />
      <Route path="/terms" element={withScrollTracking(Terms)} />
      <Route path="/privacy" element={withScrollTracking(Privacy)} />
      <Route path="/disclaimer" element={withScrollTracking(Disclaimer)} />
      <Route path="*" element={withScrollTracking(NotFound)} />
    </Routes>
  );
};

export default AppRoutes;